import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_TALENT_BOARD_STATS } from '@bloomays-lib/adapter.api-talent';
import { ApolloGqlTalentBoardStats } from '@bloomays-lib/types.shared';
import { Logger } from '../services/service-logger';
import useTalentBoardStatsStore from '../stores/talentBoardStatsStore';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';

const logger = Logger('useTalentBoardStats');

type GetTalentBoardStatsResponse = {
  getTalentBoardStats: ApolloGqlTalentBoardStats | null;
};

export const useTalentBoardStats = (jobId?: string) => {
  const { getStats, setStats, mustRefreshStats } = useTalentBoardStatsStore();
  const [fetchStats, { data, loading, error, refetch }] = useLazyQuery<GetTalentBoardStatsResponse>(
    GET_TALENT_BOARD_STATS,
    {
      context: { clientName: 'api.talents' },
      fetchPolicy: 'cache-and-network',
    },
  );

  const cachedStats = jobId ? getStats(jobId) : undefined;

  useEffect(() => {
    if (!jobId) {
      return;
    }
    const shouldFetch = mustRefreshStats(jobId);
    if (shouldFetch) {
      logger.debug('Fetching talent board stats', { jobId });
      void fetchStats({ variables: { jobId } });
    }
  }, [jobId, fetchStats, mustRefreshStats]);

  useEffect(() => {
    if (error && jobId) {
      logger.error('Error fetching talent board stats', { jobId }, error);
      monitorException(error, { name: 'useTalentBoardStats', jobId });
    }
  }, [error, jobId]);

  useEffect(() => {
    if (jobId && data?.getTalentBoardStats) {
      setStats(jobId, data.getTalentBoardStats);
    }
  }, [data, jobId, setStats]);

  return {
    stats: cachedStats ?? data?.getTalentBoardStats ?? null,
    loading: Boolean((loading && !cachedStats) || (!cachedStats && !jobId)),
    error,
    refetch,
  };
};

export default useTalentBoardStats;
